/**
 * Signal History Table
 * Displays recent forex trading signals with confidence scores
 */

import { useEffect, useState } from 'react';
import { fetchRecentSignals, type ForexSignal } from '../services/forexService';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { safeFormatTimestamp } from '../lib/accountFormatters';

export function SignalHistoryTable() {
  const [signals, setSignals] = useState<ForexSignal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSignals = async () => {
      try {
        const data = await fetchRecentSignals(20);
        setSignals(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load signals');
      } finally {
        setLoading(false);
      }
    };

    loadSignals();
    const interval = setInterval(loadSignals, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-4">
        <div className="animate-pulse space-y-2">
          <div className="h-6 w-32 bg-neutral-800 rounded"></div>
          <div className="h-32 w-full bg-neutral-800 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-900 bg-red-950/20 p-4">
        <div className="text-red-400 text-sm">{error}</div>
      </div>
    );
  }

  if (signals.length === 0) {
    return (
      <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-4">
        <h3 className="text-sm font-medium text-neutral-400 mb-3">Signal History</h3>
        <div className="text-neutral-500 text-sm text-center py-8">
          No signals generated yet
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-4">
      <h3 className="text-sm font-medium text-neutral-400 mb-3">
        Signal History ({signals.length})
      </h3>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-neutral-800">
              <th className="text-left py-2 px-2 text-neutral-500 font-medium">Time</th>
              <th className="text-left py-2 px-2 text-neutral-500 font-medium">Symbol</th>
              <th className="text-center py-2 px-2 text-neutral-500 font-medium">Direction</th>
              <th className="text-right py-2 px-2 text-neutral-500 font-medium">Confidence</th>
              <th className="text-center py-2 px-2 text-neutral-500 font-medium">Regime</th>
            </tr>
          </thead>
          <tbody>
            {signals.map((signal, idx) => (
              <tr
                key={idx}
                className="border-b border-neutral-800/50 hover:bg-neutral-800/30 transition-colors"
              >
                <td className="py-2 px-2 text-neutral-400">
                  {safeFormatTimestamp(signal.timestamp, { mode: 'time', fallback: '—', invalidFallback: '—' })}
                </td>
                <td className="py-2 px-2 text-neutral-300 font-medium">
                  {signal.symbol}
                </td>
                <td className="py-2 px-2 text-center">
                  <div className="flex items-center justify-center gap-1">
                    {signal.direction === 'long' ? (
                      <>
                        <TrendingUp className="w-3 h-3 text-green-400" />
                        <span className="text-green-400 font-medium">LONG</span>
                      </>
                    ) : (
                      <>
                        <TrendingDown className="w-3 h-3 text-red-400" />
                        <span className="text-red-400 font-medium">SHORT</span>
                      </>
                    )}
                  </div>
                </td>
                <td className="py-2 px-2 text-right">
                  <span
                    className={
                      signal.confidence >= 0.7
                        ? 'text-green-400'
                        : signal.confidence >= 0.6
                        ? 'text-yellow-400'
                        : 'text-neutral-400'
                    }
                  >
                    {(signal.confidence * 100).toFixed(1)}%
                  </span>
                </td>
                <td className="py-2 px-2 text-center">
                  <span className="text-neutral-500 capitalize text-[10px]">
                    {signal.regime_state}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
